/**
 * TemperatureMaturityChart - temperature and maturity of each sensor cylinder
 * on one chart (temperature on the left axis, maturity on the right), with a
 * marker at every test time. System calibrations only: the data comes from
 * the calibration pour's loggers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import calibrationsService, {
  CalibrationSample,
  TemperatureMaturityData,
} from '../../services/calibrations';
import { apiErrorText } from '../../utils/apiError';
import { utcNaiveToDate } from './calibrationFormat';

const COLORS = ['#1f77b4', '#d62728', '#2ca02c', '#9467bd'];

interface Props {
  calId: string;
  samples: CalibrationSample[];
  /** Changes whenever the pour or specimens change, to reload. */
  reloadKey?: string | number;
}

const TemperatureMaturityChart: React.FC<Props> = ({ calId, samples, reloadKey }) => {
  const [data, setData] = useState<TemperatureMaturityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    calibrationsService
      .getTemperatureMaturity(calId)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(apiErrorText(e, 'The sensor history could not be loaded')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [calId, reloadKey]);

  const options = useMemo<Highcharts.Options | null>(() => {
    if (!data || data.series.length === 0) return null;
    const unit = data.unit || '';
    const series: Highcharts.SeriesOptionsType[] = [];
    data.series.forEach((s, i) => {
      const color = COLORS[i % COLORS.length];
      const pts = s.points.map((p) => ({ x: utcNaiveToDate(p[0]).getTime(), t: p[1], m: p[2] }));
      const label = `Specimen ${s.slot}`;
      series.push({
        type: 'line', name: `${label} temperature`, color, yAxis: 0,
        data: pts.map((p) => [p.x, p.t]), tooltip: { valueSuffix: ' °C' },
        dashStyle: s.accepted ? 'Solid' : 'ShortDot',
      });
      series.push({
        type: 'line', name: `${label} maturity`, color, yAxis: 1, dashStyle: 'Dash',
        data: pts.map((p) => [p.x, p.m]), tooltip: { valueSuffix: ` ${unit}` },
      });
    });
    const testLines = samples
      .filter((s) => s.sampled_time)
      .map((s) => ({
        // sampled_time is the user's local entry, which Date parses as local.
        value: new Date(s.sampled_time as string).getTime(),
        color: '#9e9e9e',
        dashStyle: 'Dot' as Highcharts.DashStyleValue,
        width: 1,
        zIndex: 3,
        label: {
          text: s.avg_strength != null ? `${s.avg_strength}` : 'test',
          rotation: 0,
          style: { fontSize: '10px', color: '#616161' },
        },
      }));
    return {
      chart: { height: 300, zooming: { type: 'x' } },
      title: { text: undefined },
      credits: { enabled: false },
      time: { useUTC: false } as Highcharts.TimeOptions,
      xAxis: { type: 'datetime', plotLines: testLines },
      yAxis: [
        { title: { text: 'Temperature (°C)' } },
        { title: { text: `Maturity (${unit})` }, opposite: true, min: 0 },
      ],
      tooltip: { shared: true, xDateFormat: '%d/%m/%Y %H:%M' },
      legend: { itemStyle: { fontSize: '11px', fontWeight: '400' } },
      series,
    };
  }, [data, samples]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!options) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Choose the calibration pour and the sensor cylinders to see their temperature and maturity here.
      </Typography>
    );
  }
  return (
    <Box>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};

export default TemperatureMaturityChart;
